import { cn } from 'cn';
import type React from 'react';
import { Button, type ButtonTheme as Theme } from '#ui/components/Button';
import Icon, { type IconType } from '#ui/components/Icon';
import Tooltip from '../Tooltip';

export type WidgetProps = {
  title: string;
  description?: React.ReactNode;
  icon?: IconType;
  className?: string;
  children?: React.ReactNode;
  expired?: boolean;
  'data-testid'?: string;
  actions?: ({
    text?: string;
    theme?: Theme;
    icon?: IconType;
    disabled?: boolean;
    onClick?: () => void;
  } & ({ customElement?: never } | { customElement: React.ReactNode }))[];
};

function Widget(props: WidgetProps) {
  const { 'data-testid': dataTestId, ...restProps } = props;
  return (
    <section
      className={cn(
        'border-neutral-10 p-6 md:shadow-sm-dl-8 relative col-span-12 bg-white md:rounded-md md:border space-y-6',
        ' kunde:border-b kunde:border-neutral-10 md:kunde:border',
        restProps.className,
      )}
      data-testid={dataTestId}
    >
      <div className={cn('flex items-start justify-between')}>
        <h2 className="typ-headline-md flex items-center">
          <div
            className={cn(
              'mr-4 hidden size-10 items-center justify-center rounded-full md:flex',
              restProps.expired ? 'bg-neutral-100' : 'bg-blue-60',
            )}
          >
            <Icon
              type={restProps.icon}
              size="small"
              className="text-white"
              solid
            />
          </div>
          {restProps.title}
        </h2>

        {restProps.actions && restProps.actions.length > 0 && (
          <div className="ml-auto hidden items-center space-x-4 md:flex">
            {restProps.actions.map((action, index) =>
              action.customElement ? (
                <div key={index}>{action.customElement}</div>
              ) : (
                <Button
                  key={index}
                  theme={action.theme}
                  disabled={action.disabled}
                  onClick={action.onClick}
                  className={action.icon ? 'flex items-center gap-2' : ''}
                >
                  {action.text}
                  {action.icon && <Icon type={action.icon} />}
                </Button>
              ),
            )}
          </div>
        )}
      </div>

      {restProps.description && (
        <p className="prose  text-neutral-110 max-w-155">
          {restProps.description}
        </p>
      )}

      {restProps.children}
    </section>
  );
}

export type DataEntry = {
  id: string;
  label?: string;
  tooltip?: string;
  value?: React.ReactNode | string;
  rightAlign?: boolean;
  action?: React.ReactNode;
  hide?: boolean;
};

function KeyValuePairs({
  entries,
  className,
  childrenOnly = false,
}: {
  entries?: Array<DataEntry>;
  className?: string;
  childrenOnly?: boolean;
}) {
  const items = <>{entries?.map(renderDataEntry)}</>;

  if (childrenOnly) {
    return items;
  }

  return <dl className={cn('grid gap-x-8', className)}>{items}</dl>;
}

function renderDataEntry(
  { id, label, value, tooltip, rightAlign, action, hide }: DataEntry,
  index: number,
): React.ReactNode {
  return (
    <div
      className={cn(
        'border-neutral-10 odd:bg-neutral-5 grid border-b p-4 md:grid md:grid-cols-2 md:gap-4 last-of-type:border-b-0',
        hide && 'hidden!',
      )}
      key={id + index}
    >
      <dt className="items-top inline-flex flex-wrap gap-1">
        <span className="text-neutral-110">{label}</span>
        {tooltip ? (
          <Tooltip tooltip={tooltip}>
            <div className="block">
              <Icon type="info" className="text-neutral-100" />
            </div>
          </Tooltip>
        ) : null}
      </dt>

      <dd
        className={cn(
          'flex w-full items-center md:w-auto',
          rightAlign ? 'md:justify-end' : 'justify-start',
        )}
        data-testid={`${id}-value`}
      >
        <span
          className={cn(
            'flex-1 overflow-hidden wrap-break-word',
            rightAlign ? 'md:text-right' : 'text-left',
          )}
        >
          {value}
        </span>
        {action && (
          <span className="ml-4 hidden shrink-0 md:inline">{action}</span>
        )}
      </dd>
    </div>
  );
}

Widget.KeyValuePairs = KeyValuePairs;
export default Widget;
